import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { opportunityApi } from '../services/api';
import { Opportunity } from '../types';
import { TYPE_LABELS, REWARD_LABELS, RISK_LABELS, DIFFICULTY_LABELS } from '../data/opportunities';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

const REPORT_REASONS = [
  { value: 'expirada', label: 'A oportunidade expirou' },
  { value: 'link-quebrado', label: 'Link quebrado' },
  { value: 'golpe', label: 'Suspeita de golpe' },
  { value: 'dados-incorretos', label: 'Dados incorretos' },
  { value: 'outro', label: 'Outro problema' },
];

const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) =>
  value ? (
    <div className="flex justify-between gap-4 py-2 border-b border-gray-50 last:border-0 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-800 font-medium text-right">{value}</span>
    </div>
  ) : null;

export const OportunidadePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('expirada');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSent, setReportSent] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    opportunityApi
      .getOpportunityBySlug(slug)
      .then(setOpportunity)
      .catch(() => setOpportunity(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const submitReport = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!slug) return;
    try {
      await opportunityApi.reportProblem(slug, reportReason, reportDetails || undefined);
      setReportSent(true);
    } catch {
      setReportSent(true); // não expor erro interno; reporte é best-effort
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Carregando oportunidade...</div>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Oportunidade não encontrada</h1>
          <Link to="/oportunidades" className="text-blue-600 hover:underline">Ver todas as oportunidades</Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `${SITE_URL}/oportunidades/${opportunity.slug}`;
  const isExpired = opportunity.status === 'expired' ||
    (opportunity.deadline ? new Date(opportunity.deadline) < new Date() : false);
  const risk = RISK_LABELS[opportunity.riskLevel];
  const reward = REWARD_LABELS[opportunity.rewardClass];

  const jsonLd: any[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${opportunity.name} — ${TYPE_LABELS[opportunity.type]}`,
      description: opportunity.description,
      url: canonicalUrl,
      datePublished: opportunity.createdAt,
      dateModified: opportunity.updatedAt,
      inLanguage: 'pt-BR',
      author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
      publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Oportunidades', item: `${SITE_URL}/oportunidades` },
        { '@type': 'ListItem', position: 3, name: opportunity.name, item: canonicalUrl },
      ],
    },
  ];
  if (opportunity.faq && opportunity.faq.length > 0) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: opportunity.faq.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }

  return (
    <>
      <Helmet>
        <title>{opportunity.name} — {TYPE_LABELS[opportunity.type]} | {SITE_NAME}</title>
        <meta name="description" content={opportunity.description.slice(0, 158)} />
        <link rel="canonical" href={canonicalUrl} />
        {isExpired && <meta name="robots" content="noindex" />}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={`${opportunity.name} — ${TYPE_LABELS[opportunity.type]}`} />
        <meta property="og:description" content={opportunity.description.slice(0, 158)} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <Link to="/oportunidades" className="hover:text-blue-600 transition">Oportunidades</Link>
            <span>›</span>
            <span className="text-gray-700 truncate max-w-xs">{opportunity.name}</span>
          </nav>

          {isExpired && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 mb-6 text-red-800 text-sm font-semibold">
              ⛔ Esta oportunidade está encerrada. A página permanece disponível para referência.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {/* Header */}
              <div className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded-full text-xs font-bold">
                    {TYPE_LABELS[opportunity.type]}
                  </span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${risk.className}`}>{risk.label}</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${reward.className}`}>{reward.label}</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{opportunity.name}</h1>
                <p className="text-gray-600 leading-relaxed mb-4">{opportunity.description}</p>

                {opportunity.fullDescription && (
                  <div className="text-gray-700 text-[15px] leading-relaxed space-y-3 mb-4">
                    {opportunity.fullDescription.split(/\n\n+/).map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                )}

                <a
                  href={opportunity.officialUrl}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-700 transition text-sm"
                >
                  Fonte oficial
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <p className="text-xs text-gray-400 mt-2 break-all">{opportunity.officialUrl}</p>
              </div>

              {/* Requisitos */}
              {opportunity.requirements.length > 0 && (
                <section className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-3">Requisitos</h2>
                  <ul className="space-y-2">
                    {opportunity.requirements.map((requirement, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700 text-[15px] leading-relaxed">
                        <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-blue-500" aria-hidden />
                        <span>{requirement}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Passo a passo */}
              {opportunity.steps.length > 0 && (
                <section className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-3">Passo a passo geral</h2>
                  <ol className="space-y-3">
                    {opportunity.steps.map((step, index) => (
                      <li key={index} className="flex items-start gap-3 text-gray-700 text-[15px] leading-relaxed">
                        <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                          {index + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              {/* Riscos */}
              <section className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Riscos e classificação</h2>
                <p className="mb-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${risk.className}`}>{risk.label}</span>
                </p>
                {opportunity.riskNotes ? (
                  <p className="text-gray-700 text-[15px] leading-relaxed">{opportunity.riskNotes}</p>
                ) : (
                  <p className="text-gray-700 text-[15px] leading-relaxed">
                    Classificação atribuída conforme nossa metodologia pública de avaliação de risco.
                  </p>
                )}
                <p className="text-sm text-gray-500 mt-3">
                  Entenda os critérios na página do{' '}
                  <Link to="/oportunidades" className="text-blue-600 hover:underline">Radar de Oportunidades</Link>.
                  Lembre-se: nunca informe sua seed phrase; use carteira separada para campanhas.
                </p>
              </section>

              {/* FAQ */}
              {opportunity.faq && opportunity.faq.length > 0 && (
                <section className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">Perguntas frequentes</h2>
                  <div className="space-y-5">
                    {opportunity.faq.map((faq, index) => (
                      <div key={index}>
                        <h3 className="font-semibold text-gray-900 mb-1">{faq.question}</h3>
                        <p className="text-gray-600 text-[15px] leading-relaxed">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Fontes complementares */}
              {opportunity.extraSources && opportunity.extraSources.length > 0 && (
                <section className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-3">Fontes complementares</h2>
                  <ul className="space-y-2">
                    {opportunity.extraSources.map((source) => (
                      <li key={source.url}>
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="text-blue-600 hover:underline text-sm"
                        >
                          {source.name} ↗
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 leading-relaxed">
                <strong>⚠️ Aviso:</strong> conteúdo informativo verificado na data indicada. Condições, prazos
                e recompensas podem mudar sem aviso; golpistas imitam campanhas legítimas — use sempre o link
                oficial. Recompensas estimadas não são garantidas. Não é recomendação de investimento.
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow p-6">
                <h2 className="font-bold text-gray-900 mb-2">Ficha técnica</h2>
                <InfoRow label="Projeto" value={opportunity.project} />
                <InfoRow label="Blockchain" value={opportunity.blockchain} />
                <InfoRow label="Custo obrigatório" value={opportunity.mandatoryCost || 'Nenhum'} />
                <InfoRow label="Custo estimado" value={opportunity.estimatedCost} />
                <InfoRow label="Tempo estimado" value={opportunity.estimatedTime} />
                <InfoRow label="Dificuldade" value={DIFFICULTY_LABELS[opportunity.difficulty]} />
                <InfoRow label="Recompensa confirmada" value={opportunity.confirmedReward} />
                <InfoRow label="Recompensa possível" value={opportunity.possibleReward} />
                <InfoRow label="Tipo de recompensa" value={opportunity.rewardType} />
                <InfoRow label="Carteira" value={opportunity.needsWallet ? 'Necessária' : 'Não necessária'} />
                <InfoRow label="KYC" value={opportunity.needsKyc ? 'Exigido' : 'Não exigido'} />
                <InfoRow label="Países elegíveis" value={opportunity.eligibleCountries} />
                <InfoRow
                  label="Início"
                  value={opportunity.startDate ? format(new Date(opportunity.startDate), 'dd/MM/yyyy') : undefined}
                />
                <InfoRow
                  label="Prazo"
                  value={opportunity.deadline ? format(new Date(opportunity.deadline), 'dd/MM/yyyy') : 'Indeterminado'}
                />
              </div>

              <div className="bg-white rounded-xl shadow p-6">
                <h2 className="font-bold text-gray-900 mb-2">Verificação editorial</h2>
                <p className="text-sm text-gray-700 mb-1">
                  Última verificação:{' '}
                  <strong>{format(new Date(opportunity.lastVerifiedAt), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}</strong>
                </p>
                {opportunity.reviewedBy && (
                  <p className="text-sm text-gray-700 mb-3">Revisado por: {opportunity.reviewedBy}</p>
                )}
                {opportunity.editorialNotes && (
                  <p className="text-sm text-gray-600 leading-relaxed mb-3">{opportunity.editorialNotes}</p>
                )}

                {!showReport && !reportSent && (
                  <button
                    onClick={() => setShowReport(true)}
                    className="w-full border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg px-4 py-2 hover:bg-gray-50 transition"
                  >
                    🚩 Reportar problema
                  </button>
                )}

                {showReport && !reportSent && (
                  <form onSubmit={submitReport} className="space-y-3">
                    <select
                      value={reportReason}
                      onChange={(e) => setReportReason(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      {REPORT_REASONS.map((reason) => (
                        <option key={reason.value} value={reason.value}>{reason.label}</option>
                      ))}
                    </select>
                    <textarea
                      value={reportDetails}
                      onChange={(e) => setReportDetails(e.target.value)}
                      placeholder="Detalhes (opcional)"
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                    <button
                      type="submit"
                      className="w-full bg-blue-600 text-white text-sm font-semibold rounded-lg px-4 py-2 hover:bg-blue-700 transition"
                    >
                      Enviar reporte
                    </button>
                  </form>
                )}

                {reportSent && (
                  <p className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
                    ✅ Obrigado! Seu reporte foi registrado e será analisado pela equipe editorial.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
